import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export const SITE_NAME = "Chararo";
export const DEFAULT_TITLE = "Chararo - AI Character Chat";
export const DEFAULT_DESCRIPTION =
  "Chararo - discover AI characters, chat in immersive roleplay and create your own stories.";
const SITE_ORIGIN = (
  process.env.REACT_APP_SITE_ORIGIN || "https://chararo.ai"
).replace(/\/+$/, "");

function ensure(selector, tag, attrs) {
  let el = document.head.querySelector(selector);
  const created = !el;
  if (created) {
    el = document.createElement(tag);
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  return { el, created };
}

/**
 * Keeps <title>, description, canonical and robots in sync with the current client-side route.
 * The Worker already serves the right tags for the first page load; this covers in-app navigation.
 * Pass null to leave the document untouched. Everything is restored when the page unmounts.
 */
export default function useDocumentMeta(meta) {
  const active = Boolean(meta);
  const { title, description, canonicalPath, noindex } = meta || {};
  useEffect(() => {
    if (!active) return undefined;
    const previousTitle = document.title;
    if (title) document.title = title;

    const restores = [];
    const setAttr = (selector, tag, attrs, attr, value) => {
      const { el, created } = ensure(selector, tag, attrs);
      const before = el.getAttribute(attr);
      el.setAttribute(attr, value);
      restores.push(() => {
        if (created) el.remove();
        else if (before === null) el.removeAttribute(attr);
        else el.setAttribute(attr, before);
      });
    };
    if (description) {
      setAttr(
        'meta[name="description"]',
        "meta",
        { name: "description" },
        "content",
        description,
      );
    }
    if (canonicalPath) {
      setAttr(
        'link[rel="canonical"]',
        "link",
        { rel: "canonical" },
        "href",
        SITE_ORIGIN + canonicalPath,
      );
    }
    if (noindex) {
      setAttr(
        'meta[name="robots"]',
        "meta",
        { name: "robots" },
        "content",
        "noindex,follow",
      );
    }
    return () => {
      document.title = previousTitle;
      restores.reverse().forEach((restore) => restore());
    };
  }, [active, title, description, canonicalPath, noindex]);
}

const TITLES = {
  "/terms-of-use": [
    `Terms of Use - ${SITE_NAME}`,
    `The terms that apply when you use ${SITE_NAME}.`,
  ],
  "/privacy-policy": [
    `Privacy Policy - ${SITE_NAME}`,
    `How ${SITE_NAME} collects, uses and protects your information.`,
  ],
};

function titleCase(text) {
  return text.replace(/(^|\s)(\p{L})/gu, (_m, space, letter) => space + letter.toUpperCase());
}

/** Route-level metadata for pages whose content does not need to load data first. */
export function routeMeta(pathname, search) {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : "/";
  if (path.startsWith("/character/")) return null; // set by CharacterDetailPage
  if (path === "/") {
    const params = new URLSearchParams(search);
    const tag = (params.get("tag") || "").trim().toLowerCase();
    const refined = ["q", "sort", "page"].some((k) => params.has(k));
    if (tag && !refined) {
      const name = titleCase(tag);
      return {
        title: `${name} AI Characters - ${SITE_NAME}`,
        description: `Browse ${name.toLowerCase()} AI characters on ${SITE_NAME}. Pick a character, start a roleplay chat and explore new stories.`,
        canonicalPath: `/?tag=${encodeURIComponent(tag)}`,
      };
    }
    return {
      title: DEFAULT_TITLE,
      description: DEFAULT_DESCRIPTION,
      canonicalPath: "/",
      noindex: refined || Boolean(tag),
    };
  }
  if (TITLES[path]) {
    return {
      title: TITLES[path][0],
      description: TITLES[path][1],
      canonicalPath: path,
    };
  }
  // Search, chat and library pages are personal or endless; keep them out of the index.
  return {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    canonicalPath: "/",
    noindex: true,
  };
}

export function RouteMeta() {
  const { pathname, search } = useLocation();
  useDocumentMeta(routeMeta(pathname, search));
  return null;
}
